import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { CalendarIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import {
  DatePicker as ShopifyDatePicker,
  BlockStack,
  Popover,
  TextField,
  Icon,
  Card,
} from "@shopify/polaris";
import useIsPhone from "features/files/use-is-phone";

function DatePicker({
  selected,
  onChange,
  title = "",
  isRequired = false,
  // A form that checks its own fields (FormFunnel does it on "Next") passes its message here, and
  // "" while it has none. Without it the picker flags an empty required date as soon as it is drawn.
  error: formError,
  disableDatesAfter,
  disableDatesBefore = undefined,
  readOnly = false,
  fullWidth = true,
}) {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();
  // The calendar can only move month by month, so a date years away is typed (on a phone the
  // numeric keypad opens). The keypad covers what is under the field, so there the calendar opens above it.
  const isPhone = useIsPhone();
  const [selectedDate, setSelectedDate] = useState(
    selected ? new Date(selected) : null
  );

  useEffect(() => {
    const newDate = selected ? new Date(selected) : null;
    setSelectedDate(newDate);
    setInputValue(newDate ? newDate.toISOString().split("T")[0] : "-");
  }, [selected]);

  const [{ month, year }, setDate] = useState({
    month: selectedDate ? selectedDate.getMonth() : new Date().getMonth(),
    year: selectedDate ? selectedDate.getFullYear() : new Date().getFullYear(),
  });
  const [inputValue, setInputValue] = useState(
    selectedDate ? selectedDate.toISOString().split("T")[0] : "-"
  );
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setVisible(false);
      }
    };
    // pointerdown, not mousedown: a tap on a non-clickable area of an iPhone sends no mouse events.
    document.addEventListener("pointerdown", handleClickOutside);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [datePickerRef]);

  function formatDateString(value) {
    // Remove all non-digit characters
    const cleanValue = value.replace(/\D/g, "");

    let year = cleanValue.slice(0, 4);
    let month = cleanValue.slice(4, 6);
    let day = cleanValue.slice(6, 8);

    // Limit month between 1 and 12
    if (month && parseInt(month, 10) > 12) {
      month = "12";
    }

    // Limit day based on month
    if (day) {
      const maxDaysInMonth = new Date(
        parseInt(year, 10),
        parseInt(month, 10),
        0
      ).getDate();
      if (parseInt(day, 10) > maxDaysInMonth) {
        day = maxDaysInMonth.toString();
      }
    }

    let formattedValue = year;
    if (month) {
      formattedValue += `-${month}`;
    }
    if (day) {
      formattedValue += `-${day}`;
    }

    return formattedValue;
  }

  function handleInputValueChange(value) {
    const formattedValue = formatDateString(value);
    setInputValue(formattedValue);

    // Complete date length: YYYY-MM-DD
    if (formattedValue.length === 10) {
      const [year, month, day] = formattedValue.split("-").map(Number);
      const newDate = new Date(Date.UTC(year, month - 1, day));
      if (!isNaN(newDate.getTime())) {
        // Reject dates before the boundary
        const normalizedDisableDatesBefore = disableDatesBefore
          ? new Date(
              Date.UTC(
                disableDatesBefore.getFullYear(),
                disableDatesBefore.getMonth(),
                disableDatesBefore.getDate()
              )
            )
          : null;
        if (normalizedDisableDatesBefore && newDate < normalizedDisableDatesBefore) {
          return;
        }
        // Reject dates after the boundary
        const normalizedDisableDatesAfter = disableDatesAfter
          ? new Date(
              Date.UTC(
                disableDatesAfter.getFullYear(),
                disableDatesAfter.getMonth(),
                disableDatesAfter.getDate()
              )
            )
          : null;
        if (normalizedDisableDatesAfter && newDate > normalizedDisableDatesAfter) {
          return;
        }
        setSelectedDate(newDate);
        setDate({
          month: newDate.getMonth(),
          year: newDate.getFullYear(),
        });
        onChange(newDate);
      }
    } else if (value === "") {
      setInputValue("-");
      setSelectedDate(null);
      onChange(null);
    }
  }

  function handleOnClose({ relatedTarget }) {
    setVisible(false);
  }
  function handleMonthChange(month, year) {
    setDate({ month, year });
  }
  function handleDateSelection({ end: newSelectedDate }) {
    const newDate = new Date(
      Date.UTC(
        newSelectedDate.getFullYear(),
        newSelectedDate.getMonth(),
        newSelectedDate.getDate()
      )
    );
    setSelectedDate(newSelectedDate);
    setInputValue(newDate.toISOString().split("T")[0]);
    setVisible(false);
    onChange(newDate);
  }
  useEffect(() => {
    if (selectedDate) {
      setDate({
        month: selectedDate.getMonth(),
        year: selectedDate.getFullYear(),
      });
    }
    // check error
    errorHandler(selectedDate);
  }, [isRequired, selectedDate]);

  const [requiredError, setRequiredError] = useState("");
  const errorHandler = (val) => {
    if (isRequired && !val) {
      setRequiredError(t("field_is_required"));
    } else {
      setRequiredError("");
    }
  };

  return (
    <BlockStack inlineAlign="left" gap="0">
      <div className="sm:min-w-[275px]">
        <Popover
          active={visible}
          autofocusTarget="none"
          preferredAlignment="left"
          fullWidth={fullWidth}
          preferInputActivator={false}
          preferredPosition={isPhone ? "above" : "below"}
          preventCloseOnChildOverlayClick
          onClose={handleOnClose}
          activator={
            <div
              style={fullWidth ? {} : { maxWidth: "300px" }}
              onClick={() => setVisible(true)}
            >
              <TextField
                role="combobox"
                label={title || "Start date"}
                prefix={<Icon source={CalendarIcon} />}
                value={inputValue}
                onFocus={() => setVisible(true)}
                onChange={handleInputValueChange}
                autoComplete="off"
                placeholder="YYYY-MM-DD"
                inputMode="numeric"
                // On a phone, one tap empties an optional date instead of many backspaces.
                clearButton={isPhone && !isRequired && !readOnly && !!selectedDate}
                onClearButtonClick={() => handleInputValueChange("")}
                requiredIndicator={isRequired ? true : false}
                error={formError ?? requiredError}
                readOnly={readOnly}
              />
            </div>
          }
        >
          <div ref={datePickerRef}>
            {!readOnly && (
              <Card>
                <ShopifyDatePicker
                  month={month}
                  year={year}
                  selected={selectedDate}
                  onMonthChange={handleMonthChange}
                  onChange={handleDateSelection}
                  weekStartsOn={1}
                  disableDatesBefore={
                    disableDatesBefore || undefined
                  }
                  disableDatesAfter={disableDatesAfter || undefined}
                />
              </Card>
            )}
          </div>
        </Popover>
      </div>
    </BlockStack>
  );
}

export default DatePicker;

DatePicker.propTypes = {
  selected: PropTypes.string,
  error: PropTypes.string,
  // disableDatesBefore: PropTypes.string.isRequired,
  // disableDatesAfter is intentionally left out of propTypes: TS infers JS
  // component prop types from propTypes, and this prop is passed a plain
  // Date in some call sites but a boolean/string flag in others across the
  // app, so declaring a strict type here breaks `tsc` for unrelated files.
  onChange: PropTypes.func.isRequired,
};
